/**
 * ElementPropertyEditor — renders the property editor for a single scenario
 * element inside the right panel. Receives the element, scenes list, and
 * dispatch callbacks; zero Redux connection so it renders cleanly in any context.
 */
import React from "react";
import i18n from "i18next";
import { makeId } from "../schema";
import { ActionListEditor } from "./ActionEditor";

/* eslint-disable react/prop-types */

const TYPE_ICONS = {
  visualtext: "article",
  visualimage: "image",
  videocard: "videocam",
  flipcard: "flip",
  truefalse: "rule",
  mcq: "quiz",
};

const TYPE_LABELS = {
  visualtext: i18n.t("InteractiveScenario.Type.VisualText") || "Metin Kartı",
  visualimage: i18n.t("InteractiveScenario.Type.VisualImage") || "Görsel Kart",
  videocard: i18n.t("InteractiveScenario.Type.VideoCard") || "Video Kart",
  flipcard: i18n.t("InteractiveScenario.Type.FlipCard") || "Çift Yüzlü Kart",
  truefalse: i18n.t("InteractiveScenario.Type.TrueFalse") || "Doğru / Yanlış",
  mcq: i18n.t("InteractiveScenario.Type.MCQ") || "Çoktan Seçmeli",
};

const BUTTON_STYLES = [
  {
    value: "primary",
    label: i18n.t("InteractiveScenario.ButtonStyle.Primary") || "Birincil",
    color: "#3b82f6",
  },
  {
    value: "secondary",
    label: i18n.t("InteractiveScenario.ButtonStyle.Secondary") || "İkincil",
    color: "#64748b",
  },
  {
    value: "success",
    label: i18n.t("InteractiveScenario.ButtonStyle.Success") || "Başarı",
    color: "#22c55e",
  },
  {
    value: "danger",
    label: i18n.t("InteractiveScenario.ButtonStyle.Danger") || "Tehlike",
    color: "#ef4444",
  },
];

function Section({ children }) {
  return <div className="sce-element-row__section">{children}</div>;
}

const ALIGN_OPTIONS = [
  { value: "left", icon: "format_align_left" },
  { value: "center", icon: "format_align_center" },
  { value: "right", icon: "format_align_right" },
];

function AlignPicker({ value, onChange }) {
  return (
    <div style={{ display: "flex", gap: 4, marginTop: 4 }}>
      {ALIGN_OPTIONS.map((opt) => (
        <button
          key={opt.value}
          type="button"
          onClick={() => onChange(opt.value)}
          title={opt.value}
          style={{
            width: 30,
            height: 28,
            border: "1px solid",
            borderColor: value === opt.value ? "#a963f5" : "#e2e8f0",
            borderRadius: 6,
            background: value === opt.value ? "#f3e7ff" : "#f8fafc",
            color: value === opt.value ? "#a963f5" : "#94a3b8",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            transition: "all 0.12s",
          }}
        >
          <span className="material-icons" style={{ fontSize: 15 }}>
            {opt.icon}
          </span>
        </button>
      ))}
    </div>
  );
}

function ButtonsEditor({ buttons, scenes, variables, elements, setField }) {
  return (
    <div style={{ marginTop: 8 }}>
      <label className="sce-field__label">
        {i18n.t("InteractiveScenario.Label.Buttons") || "Butonlar"}
      </label>
      {(buttons || []).map((btn, bi) => (
        <div key={btn.id} className="sce-element-row__btn-block">
          <div className="sce-action-editor__row" style={{ flexWrap: "wrap", gap: 4 }}>
            <input
              className="sce-action-editor__input"
              type="text"
              placeholder={i18n.t("InteractiveScenario.Placeholder.ButtonLabel") || "Buton etiketi"}
              value={btn.label || ""}
              onChange={(e) => setField(["buttons", bi, "label"], e.target.value)}
              style={{ flex: "1 1 100px" }}
            />
            <select
              className="sce-action-editor__select"
              value={btn.style || "primary"}
              onChange={(e) => setField(["buttons", bi, "style"], e.target.value)}
              style={{ flex: "0 0 auto" }}
            >
              {BUTTON_STYLES.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="sce-action-editor__delete"
              onClick={() =>
                setField(
                  ["buttons"],
                  (buttons || []).filter((_, i) => i !== bi)
                )
              }
            >
              ✕
            </button>
          </div>
          {/* Style preview chip */}
          <div style={{ marginTop: 4, marginBottom: 2 }}>
            <span
              style={{
                display: "inline-block",
                padding: "2px 10px",
                borderRadius: 6,
                fontSize: 11,
                fontWeight: 600,
                background:
                  BUTTON_STYLES.find((s) => s.value === (btn.style || "primary"))?.color ||
                  "#3b82f6",
                color: "#fff",
                opacity: 0.85,
              }}
            >
              {btn.label || i18n.t("InteractiveScenario.Default.Button") || "Buton"}
            </span>
          </div>
          <ActionListEditor
            actions={btn.actions || []}
            scenes={scenes}
            variables={variables}
            elements={elements}
            onChange={(acts) => setField(["buttons", bi, "actions"], acts)}
          />
        </div>
      ))}
      <button
        type="button"
        className="sce-add-btn"
        style={{ marginTop: 4 }}
        onClick={() =>
          setField(
            ["buttons"],
            [
              ...(buttons || []),
              {
                id: makeId(),
                label: i18n.t("InteractiveScenario.Default.Button") || "Buton",
                style: "primary",
                actions: [],
              },
            ]
          )
        }
      >
        {i18n.t("InteractiveScenario.Action.AddButton") || "+ Buton Ekle"}
      </button>
    </div>
  );
}

export default function ElementPropertyEditor({
  element,
  scenes,
  variables,
  elements,
  setField,
  onPickFile,
}) {
  if (!element) return null;

  const { type } = element;

  const renderFields = () => {
    switch (type) {
      case "visualtext":
        return (
          <Section>
            <label className="sce-field__label">
              {i18n.t("InteractiveScenario.Label.Content") || "İçerik"}
            </label>
            <textarea
              className="sce-action-editor__input sce-action-editor__textarea"
              value={element.content || ""}
              rows={3}
              onChange={(e) => setField(["content"], e.target.value)}
            />
            <ButtonsEditor
              buttons={element.buttons}
              scenes={scenes}
              variables={variables}
              elements={elements}
              setField={setField}
            />
          </Section>
        );

      case "visualimage":
        return (
          <Section>
            <label className="sce-field__label">
              {i18n.t("InteractiveScenario.Label.Image") || "Görsel"}
            </label>
            {onPickFile && (
              <button
                type="button"
                className="sce-add-btn"
                style={{ width: "100%", justifyContent: "center", marginBottom: 4 }}
                onClick={() => onPickFile("imageUrl", "image/*")}
              >
                <span className="material-icons" style={{ fontSize: 13, marginRight: 4 }}>
                  folder_open
                </span>
                {i18n.t("InteractiveScenario.Action.SelectFromLibrary") || "Kütüphaneden Seç"}
              </button>
            )}
            <input
              className="sce-action-editor__input"
              type="text"
              placeholder={i18n.t("InteractiveScenario.Placeholder.OrEnterUrl") || "veya URL gir…"}
              value={element.imageUrl || ""}
              onChange={(e) => setField(["imageUrl"], e.target.value)}
            />
            <label className="sce-field__label" style={{ marginTop: 8 }}>
              {i18n.t("InteractiveScenario.Label.Description") || "Açıklama"}
            </label>
            <input
              className="sce-action-editor__input"
              type="text"
              placeholder={
                i18n.t("InteractiveScenario.Placeholder.ImageDescriptionOptional") ||
                "Görsel açıklaması (isteğe bağlı)"
              }
              value={element.caption || ""}
              onChange={(e) => setField(["caption"], e.target.value)}
            />
            <AlignPicker
              value={element.captionAlign || "left"}
              onChange={(v) => setField(["captionAlign"], v)}
            />
            <label
              className="sce-field__label"
              style={{ marginTop: 12, display: "flex", alignItems: "center", gap: 6 }}
            >
              <span className="material-icons" style={{ fontSize: 14 }}>
                touch_app
              </span>
              {i18n.t("InteractiveScenario.Label.ImageClickAction") || "Görsele tıklama aksiyonu"}
            </label>
            <ActionListEditor
              actions={element.imageActions || []}
              scenes={scenes}
              variables={variables}
              elements={elements}
              onChange={(acts) => setField(["imageActions"], acts)}
            />
            <ButtonsEditor
              buttons={element.buttons}
              scenes={scenes}
              variables={variables}
              elements={elements}
              setField={setField}
            />
          </Section>
        );

      case "videocard":
        return (
          <Section>
            <label className="sce-field__label">
              {i18n.t("InteractiveScenario.Label.Video") || "Video"}
            </label>
            {onPickFile && (
              <button
                type="button"
                className="sce-add-btn"
                style={{ width: "100%", justifyContent: "center", marginBottom: 4 }}
                onClick={() => onPickFile("videoUrl", "video/*")}
              >
                <span className="material-icons" style={{ fontSize: 13, marginRight: 4 }}>
                  folder_open
                </span>
                {i18n.t("InteractiveScenario.Action.SelectFromLibrary") || "Kütüphaneden Seç"}
              </button>
            )}
            <input
              className="sce-action-editor__input"
              type="text"
              placeholder={i18n.t("InteractiveScenario.Placeholder.OrEnterUrl") || "veya URL gir…"}
              value={element.videoUrl || ""}
              onChange={(e) => setField(["videoUrl"], e.target.value)}
            />
            <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 8 }}>
              <input
                type="checkbox"
                id="autoplay-toggle"
                checked={!!element.autoPlay}
                onChange={(e) => setField(["autoPlay"], e.target.checked)}
              />
              <label htmlFor="autoplay-toggle" className="sce-field__label" style={{ margin: 0 }}>
                {i18n.t("InteractiveScenario.Label.Autoplay") || "Otomatik oynat"}
              </label>
            </div>
            <label className="sce-field__label" style={{ marginTop: 8 }}>
              {i18n.t("InteractiveScenario.Label.Description") || "Açıklama"}
            </label>
            <input
              className="sce-action-editor__input"
              type="text"
              placeholder={
                i18n.t("InteractiveScenario.Placeholder.VideoDescriptionOptional") ||
                "Video açıklaması (isteğe bağlı)"
              }
              value={element.caption || ""}
              onChange={(e) => setField(["caption"], e.target.value)}
            />
            <AlignPicker
              value={element.captionAlign || "left"}
              onChange={(v) => setField(["captionAlign"], v)}
            />
            <ButtonsEditor
              buttons={element.buttons}
              scenes={scenes}
              variables={variables}
              elements={elements}
              setField={setField}
            />
          </Section>
        );

      case "flipcard":
        return (
          <Section>
            {/* Front face */}
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
              <span className="material-icons" style={{ fontSize: 13, color: "#a963f5" }}>
                flip_to_front
              </span>
              <span className="sce-field__label" style={{ margin: 0, flex: 1 }}>
                {i18n.t("InteractiveScenario.Label.FrontFace") || "Ön Yüz"}
              </span>
              <label
                title={i18n.t("InteractiveScenario.Label.BackgroundColor") || "Arkaplan rengi"}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 3,
                  cursor: "pointer",
                  fontSize: 11,
                  color: "#64748b",
                }}
              >
                <span className="material-icons" style={{ fontSize: 12 }}>
                  format_color_fill
                </span>
                <input
                  type="color"
                  value={element.frontBg || "#1e3a5f"}
                  onChange={(e) => setField(["frontBg"], e.target.value)}
                  style={{
                    width: 22,
                    height: 22,
                    border: "none",
                    padding: 0,
                    cursor: "pointer",
                    borderRadius: 4,
                  }}
                  title={
                    i18n.t("InteractiveScenario.Label.FrontFaceBackground") || "Ön yüz arkaplan"
                  }
                />
              </label>
              <label
                title={i18n.t("InteractiveScenario.Label.TextColor") || "Yazı rengi"}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 3,
                  cursor: "pointer",
                  fontSize: 11,
                  color: "#64748b",
                }}
              >
                <span className="material-icons" style={{ fontSize: 12 }}>
                  text_fields
                </span>
                <input
                  type="color"
                  value={element.frontTextColor || "#ffffff"}
                  onChange={(e) => setField(["frontTextColor"], e.target.value)}
                  style={{
                    width: 22,
                    height: 22,
                    border: "none",
                    padding: 0,
                    cursor: "pointer",
                    borderRadius: 4,
                  }}
                  title={
                    i18n.t("InteractiveScenario.Label.FrontFaceTextColor") || "Ön yüz yazı rengi"
                  }
                />
              </label>
            </div>
            {/* Front preview strip */}
            <div
              style={{
                borderRadius: 8,
                padding: "6px 10px",
                background: element.frontBg || "#1e3a5f",
                color: element.frontTextColor || "#ffffff",
                fontSize: 11,
                marginBottom: 6,
                opacity: 0.9,
              }}
            >
              {element.frontTitle || i18n.t("InteractiveScenario.Default.Title") || "Başlık"} —{" "}
              {element.frontText
                ? element.frontText.slice(0, 40) + (element.frontText.length > 40 ? "…" : "")
                : i18n.t("InteractiveScenario.Default.Text") || "Metin"}
            </div>
            <input
              className="sce-action-editor__input"
              type="text"
              placeholder={i18n.t("InteractiveScenario.Placeholder.Title") || "Başlık"}
              value={element.frontTitle || ""}
              onChange={(e) => setField(["frontTitle"], e.target.value)}
            />
            <textarea
              className="sce-action-editor__input sce-action-editor__textarea"
              placeholder={i18n.t("InteractiveScenario.Placeholder.Text") || "Metin"}
              rows={2}
              value={element.frontText || ""}
              onChange={(e) => setField(["frontText"], e.target.value)}
            />
            {/* Front image */}
            <div style={{ display: "flex", gap: 4, marginTop: 4 }}>
              {onPickFile && (
                <button
                  type="button"
                  className="sce-add-btn"
                  style={{ flex: 1, justifyContent: "center" }}
                  onClick={() => onPickFile("frontImage", "image/*")}
                >
                  <span className="material-icons" style={{ fontSize: 13, marginRight: 4 }}>
                    folder_open
                  </span>
                  {element.frontImage
                    ? i18n.t("InteractiveScenario.Action.ChangeImage") || "Görseli Değiştir"
                    : i18n.t("InteractiveScenario.Action.AddImage") || "Görsel Ekle"}
                </button>
              )}
              {element.frontImage && (
                <button
                  type="button"
                  className="sce-action-editor__delete"
                  title={i18n.t("InteractiveScenario.Action.RemoveImage") || "Görseli kaldır"}
                  onClick={() => setField(["frontImage"], null)}
                >
                  ✕
                </button>
              )}
            </div>
            {element.frontImage && (
              <img
                src={element.frontImage}
                alt=""
                style={{
                  width: "100%",
                  borderRadius: 6,
                  marginTop: 4,
                  maxHeight: 80,
                  objectFit: "cover",
                }}
              />
            )}

            {/* Back face */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                marginTop: 10,
                marginBottom: 4,
              }}
            >
              <span className="material-icons" style={{ fontSize: 13, color: "#a963f5" }}>
                flip_to_back
              </span>
              <span className="sce-field__label" style={{ margin: 0, flex: 1 }}>
                {i18n.t("InteractiveScenario.Label.BackFace") || "Arka Yüz"}
              </span>
              <label
                title={i18n.t("InteractiveScenario.Label.BackgroundColor") || "Arkaplan rengi"}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 3,
                  cursor: "pointer",
                  fontSize: 11,
                  color: "#64748b",
                }}
              >
                <span className="material-icons" style={{ fontSize: 12 }}>
                  format_color_fill
                </span>
                <input
                  type="color"
                  value={element.backBg || "#0d5c3a"}
                  onChange={(e) => setField(["backBg"], e.target.value)}
                  style={{
                    width: 22,
                    height: 22,
                    border: "none",
                    padding: 0,
                    cursor: "pointer",
                    borderRadius: 4,
                  }}
                  title={
                    i18n.t("InteractiveScenario.Label.BackFaceBackground") || "Arka yüz arkaplan"
                  }
                />
              </label>
              <label
                title={i18n.t("InteractiveScenario.Label.TextColor") || "Yazı rengi"}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 3,
                  cursor: "pointer",
                  fontSize: 11,
                  color: "#64748b",
                }}
              >
                <span className="material-icons" style={{ fontSize: 12 }}>
                  text_fields
                </span>
                <input
                  type="color"
                  value={element.backTextColor || "#ffffff"}
                  onChange={(e) => setField(["backTextColor"], e.target.value)}
                  style={{
                    width: 22,
                    height: 22,
                    border: "none",
                    padding: 0,
                    cursor: "pointer",
                    borderRadius: 4,
                  }}
                  title={
                    i18n.t("InteractiveScenario.Label.BackFaceTextColor") || "Arka yüz yazı rengi"
                  }
                />
              </label>
            </div>
            {/* Back preview strip */}
            <div
              style={{
                borderRadius: 8,
                padding: "6px 10px",
                background: element.backBg || "#0d5c3a",
                color: element.backTextColor || "#ffffff",
                fontSize: 11,
                marginBottom: 6,
                opacity: 0.9,
              }}
            >
              {element.backTitle || i18n.t("InteractiveScenario.Default.Title") || "Başlık"} —{" "}
              {element.backText
                ? element.backText.slice(0, 40) + (element.backText.length > 40 ? "…" : "")
                : i18n.t("InteractiveScenario.Default.Text") || "Metin"}
            </div>
            <input
              className="sce-action-editor__input"
              type="text"
              placeholder={i18n.t("InteractiveScenario.Placeholder.Title") || "Başlık"}
              value={element.backTitle || ""}
              onChange={(e) => setField(["backTitle"], e.target.value)}
            />
            <textarea
              className="sce-action-editor__input sce-action-editor__textarea"
              placeholder={i18n.t("InteractiveScenario.Placeholder.Text") || "Metin"}
              rows={2}
              value={element.backText || ""}
              onChange={(e) => setField(["backText"], e.target.value)}
            />
            {/* Back image */}
            <div style={{ display: "flex", gap: 4, marginTop: 4 }}>
              {onPickFile && (
                <button
                  type="button"
                  className="sce-add-btn"
                  style={{ flex: 1, justifyContent: "center" }}
                  onClick={() => onPickFile("backImage", "image/*")}
                >
                  <span className="material-icons" style={{ fontSize: 13, marginRight: 4 }}>
                    folder_open
                  </span>
                  {element.backImage
                    ? i18n.t("InteractiveScenario.Action.ChangeImage") || "Görseli Değiştir"
                    : i18n.t("InteractiveScenario.Action.AddImage") || "Görsel Ekle"}
                </button>
              )}
              {element.backImage && (
                <button
                  type="button"
                  className="sce-action-editor__delete"
                  title={i18n.t("InteractiveScenario.Action.RemoveImage") || "Görseli kaldır"}
                  onClick={() => setField(["backImage"], null)}
                >
                  ✕
                </button>
              )}
            </div>
            {element.backImage && (
              <img
                src={element.backImage}
                alt=""
                style={{
                  width: "100%",
                  borderRadius: 6,
                  marginTop: 4,
                  maxHeight: 80,
                  objectFit: "cover",
                }}
              />
            )}
            {/* Flip auto-action */}
            <label
              className="sce-field__label"
              style={{ marginTop: 12, display: "flex", alignItems: "center", gap: 6 }}
            >
              <span className="material-icons" style={{ fontSize: 14 }}>
                flip
              </span>
              {i18n.t("InteractiveScenario.Label.FlipAction") || "Çevirince tetiklenen aksiyon"}
            </label>
            <ActionListEditor
              actions={element.flipActions || []}
              scenes={scenes}
              variables={variables}
              elements={elements}
              onChange={(acts) => setField(["flipActions"], acts)}
            />

            {/* Back face button (optional) */}
            <label
              className="sce-field__label"
              style={{ marginTop: 12, display: "flex", alignItems: "center", gap: 6 }}
            >
              <span className="material-icons" style={{ fontSize: 14 }}>
                smart_button
              </span>
              {i18n.t("InteractiveScenario.Label.ActionButtonOptional") ||
                "Aksiyonlu buton (isteğe bağlı)"}
            </label>
            <input
              className="sce-action-editor__input"
              type="text"
              placeholder={i18n.t("InteractiveScenario.Placeholder.ButtonLabel") || "Buton etiketi"}
              value={element.backButtonLabel || ""}
              onChange={(e) => setField(["backButtonLabel"], e.target.value)}
            />
            <ActionListEditor
              actions={element.backActions || []}
              scenes={scenes}
              variables={variables}
              elements={elements}
              onChange={(acts) => setField(["backActions"], acts)}
            />
          </Section>
        );

      case "truefalse":
        return (
          <Section>
            <input
              className="sce-action-editor__input"
              type="text"
              placeholder={i18n.t("InteractiveScenario.Placeholder.Question") || "Soru"}
              value={element.question || ""}
              onChange={(e) => setField(["question"], e.target.value)}
            />
            <div className="sce-action-editor__row">
              <input
                className="sce-action-editor__input"
                type="text"
                placeholder={i18n.t("InteractiveScenario.Placeholder.TrueLabel") || "Doğru"}
                value={element.trueLabel || ""}
                onChange={(e) => setField(["trueLabel"], e.target.value)}
              />
              <input
                className="sce-action-editor__input"
                type="text"
                placeholder={i18n.t("InteractiveScenario.Placeholder.FalseLabel") || "Yanlış"}
                value={element.falseLabel || ""}
                onChange={(e) => setField(["falseLabel"], e.target.value)}
              />
            </div>
            <div className="sce-action-editor__row">
              <label className="sce-field__label">
                {i18n.t("InteractiveScenario.Label.CorrectAnswer") || "Doğru cevap:"}
              </label>
              <select
                className="sce-action-editor__select"
                value={element.correctAnswer === true ? "true" : "false"}
                onChange={(e) => setField(["correctAnswer"], e.target.value === "true")}
              >
                <option value="true">{i18n.t("InteractiveScenario.Option.True") || "Doğru"}</option>
                <option value="false">
                  {i18n.t("InteractiveScenario.Option.False") || "Yanlış"}
                </option>
              </select>
            </div>
            <label className="sce-field__label" style={{ marginTop: 8 }}>
              {`✓ ${i18n.t("InteractiveScenario.Label.CorrectActions") || "Doğru Aksiyonlar"}`}
            </label>
            <ActionListEditor
              actions={element.correctActions || []}
              scenes={scenes}
              variables={variables}
              elements={elements}
              onChange={(acts) => setField(["correctActions"], acts)}
            />
            <label className="sce-field__label" style={{ marginTop: 8 }}>
              {`✗ ${i18n.t("InteractiveScenario.Label.IncorrectActions") || "Yanlış Aksiyonlar"}`}
            </label>
            <ActionListEditor
              actions={element.incorrectActions || []}
              scenes={scenes}
              variables={variables}
              elements={elements}
              onChange={(acts) => setField(["incorrectActions"], acts)}
            />
            <input
              className="sce-action-editor__input"
              type="text"
              placeholder={
                i18n.t("InteractiveScenario.Placeholder.ExplanationOptional") ||
                "Açıklama (isteğe bağlı)"
              }
              style={{ marginTop: 6 }}
              value={element.explanation || ""}
              onChange={(e) => setField(["explanation"], e.target.value)}
            />
          </Section>
        );

      case "mcq":
        return (
          <Section>
            <input
              className="sce-action-editor__input"
              type="text"
              placeholder={i18n.t("InteractiveScenario.Placeholder.Question") || "Soru"}
              value={element.question || ""}
              onChange={(e) => setField(["question"], e.target.value)}
            />
            {(element.choices || []).map((choice, ci) => (
              <div key={choice.id} className="sce-element-row__choice-block">
                <div className="sce-action-editor__row">
                  <span className="sce-element-row__choice-label">{ci + 1}.</span>
                  <input
                    className="sce-action-editor__input"
                    type="text"
                    placeholder={
                      i18n.t("InteractiveScenario.Placeholder.ChoiceText") || "Seçenek metni"
                    }
                    value={choice.text || ""}
                    onChange={(e) => setField(["choices", ci, "text"], e.target.value)}
                  />
                  <span className="sce-element-row__choice-label">{ci + 1}.</span>
                  <input
                    className="sce-action-editor__input"
                    type="text"
                    placeholder={
                      i18n.t("InteractiveScenario.Placeholder.ChoiceText") || "Seçenek metni"
                    }
                    value={choice.text || ""}
                    onChange={(e) => setField(["choices", ci, "text"], e.target.value)}
                  />
                  <button
                    type="button"
                    className="sce-action-editor__delete"
                    onClick={() =>
                      setField(
                        ["choices"],
                        (element.choices || []).filter((_, i) => i !== ci)
                      )
                    }
                  >
                    ✕
                  </button>
                </div>
                <ActionListEditor
                  actions={choice.actions || []}
                  scenes={scenes}
                  variables={variables}
                  elements={elements}
                  onChange={(acts) => setField(["choices", ci, "actions"], acts)}
                />
              </div>
            ))}
            <button
              type="button"
              className="sce-add-btn"
              onClick={() =>
                setField(
                  ["choices"],
                  [...(element.choices || []), { id: makeId(), text: "", actions: [] }]
                )
              }
            >
              {i18n.t("InteractiveScenario.Action.AddChoice") || "+ Seçenek Ekle"}
            </button>
            <input
              className="sce-action-editor__input"
              type="text"
              placeholder={
                i18n.t("InteractiveScenario.Placeholder.ExplanationOptional") ||
                "Açıklama (isteğe bağlı)"
              }
              style={{ marginTop: 6 }}
              value={element.explanation || ""}
              onChange={(e) => setField(["explanation"], e.target.value)}
            />
          </Section>
        );

      default:
        return null;
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {/* Title field */}
      <div className="sce-action-editor__row" style={{ marginBottom: 2 }}>
        <span className="material-icons" style={{ fontSize: 15, color: "#a78bfa", flexShrink: 0 }}>
          {TYPE_ICONS[type] || "widgets"}
        </span>
        <input
          className="sce-action-editor__input"
          type="text"
          placeholder={i18n.t("InteractiveScenario.Placeholder.ElementTitle") || "Element başlığı"}
          value={element.title || ""}
          onChange={(e) => setField(["title"], e.target.value)}
          style={{ fontWeight: 600 }}
        />
      </div>

      {/* Hidden-by-default toggle */}
      <label
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          cursor: "pointer",
          padding: "6px 8px",
          borderRadius: 8,
          background: element.hidden ? "rgba(239,68,68,0.08)" : "#f1f5f9",
          border: `1px solid ${element.hidden ? "rgba(239,68,68,0.25)" : "#e2e8f0"}`,
          fontSize: 12,
          color: element.hidden ? "#dc2626" : "#64748b",
          userSelect: "none",
        }}
      >
        <input
          type="checkbox"
          checked={!!element.hidden}
          onChange={(e) => setField(["hidden"], e.target.checked)}
          style={{ margin: 0 }}
        />
        <span className="material-icons" style={{ fontSize: 14 }}>
          {element.hidden ? "visibility_off" : "visibility"}
        </span>
        {element.hidden
          ? i18n.t("InteractiveScenario.Label.HiddenStarts") || "Gizli başlar"
          : i18n.t("InteractiveScenario.Label.VisibleStarts") || "Görünür başlar"}
      </label>

      {/* Type-specific fields */}
      {renderFields()}
    </div>
  );
}
